import { getAvailableRoles, getCurrentRole } from "../utils/roleSwitcher";
import StorageService from "../services/StorageService";
import { SELLER_DASHBOARD_MODULE_ROUTES } from "./sellerDashboardModuleUi";

/** Same rule as sidebar `shouldShowRoleSection(currentRole, "seller")`. */
export function isSellerRoleSectionVisible(currentRole = getCurrentRole()) {
  if (currentRole == null) return true;
  return Number(currentRole.id) === 2;
}

export const SELLER_DASHBOARD_MODULES = [
  {
    key: "overview",
    label: "Overview",
    to: "/dashboard/seller",
    pathPrefixes: ["/dashboard/seller"],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
  {
    key: "rfq",
    label: "RFQ & Quotations",
    to: SELLER_DASHBOARD_MODULE_ROUTES.rfq,
    pathPrefixes: ["/dashboard/seller/rfq", "/seller-rfq-list", "/clarification-list"],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
  {
    key: "cm",
    label: "Contract Manufacturing",
    to: SELLER_DASHBOARD_MODULE_ROUTES.cm,
    pathPrefixes: ["/dashboard/seller/cm", "/seller/cm-rfqs"],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
  {
    key: "p2p",
    label: "Procure-to-Pay",
    to: SELLER_DASHBOARD_MODULE_ROUTES.p2p,
    pathPrefixes: ["/dashboard/seller/p2p", "/dashboard/p2p", "/dashboard/admin/p2p"],
    visible: (ctx) =>
      ctx.canAccessSellerDashboard && !ctx.p2pLoading && ctx.hasSellerP2pOperationalAccess,
  },
  {
    key: "infra",
    label: "Infrastructure Leasing",
    to: SELLER_DASHBOARD_MODULE_ROUTES.infra,
    pathPrefixes: [
      "/dashboard/seller/infra",
      "/infrastructure-leasing/my-properties-assets",
      "/infrastructure-leasing/inventory-operations",
      "/infrastructure-leasing/create-listing",
    ],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
  {
    key: "logistics",
    label: "Logistics",
    to: SELLER_DASHBOARD_MODULE_ROUTES.logistics,
    pathPrefixes: ["/dashboard/seller/logistics", "/logistics-rfqs-sent"],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
  {
    key: "auctions",
    label: "Auctions",
    to: SELLER_DASHBOARD_MODULE_ROUTES.auctions,
    pathPrefixes: ["/dashboard/seller/auctions", "/auction/my", "/auction/create"],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
  {
    key: "fulfilment",
    label: "Orders & Fulfilment",
    to: SELLER_DASHBOARD_MODULE_ROUTES.fulfilment,
    pathPrefixes: [
      "/dashboard/seller/fulfilment",
      "/seller-purchase-order-list",
      "/invoice-list",
      "/shipping-manifest-generation-list",
      "/approve-goods-receipt-list",
      "/eway-bill-list",
    ],
    visible: (ctx) => ctx.canAccessSellerDashboard,
  },
];

export const SELLER_DASHBOARD_SUB_TABS = {
  rfq: [
    { key: "buyer_rfqs", label: "Buyer RFQ list", to: "/seller-rfq-list" },
    { key: "cm_rfqs", label: "CM RFQs", to: "/seller/cm-rfqs" },
    { key: "clarifications", label: "Clarifications", to: "/clarification-list" },
  ],
  cm: [{ key: "cm_rfqs", label: "CM RFQs", to: "/seller/cm-rfqs" }],
  p2p: [
    {
      key: "command",
      label: "Command centre",
      to: "/dashboard/p2p/command-centre",
      visible: (ctx) => ctx.canAccessCommandCentre || ctx.isSuperAdmin,
    },
    {
      key: "approvals",
      label: "My approvals",
      to: "/dashboard/p2p/my-approvals",
      visible: (ctx) => ctx.canApprove || ctx.isSuperAdmin || ctx.isCompanyAdmin,
    },
    {
      key: "vendors",
      label: "Pre-approved vendors",
      to: "/dashboard/p2p/preapproved-vendors",
      visible: (ctx) => ctx.canAccessPreapprovedVendors || ctx.isSuperAdmin,
    },
    {
      key: "services",
      label: "Service catalogue",
      to: "/dashboard/p2p/services",
      visible: (ctx) => ctx.canSubmitServiceRequests || ctx.isSuperAdmin,
    },
  ],
  infra: [
    { key: "properties", label: "My properties", to: "/infrastructure-leasing/my-properties-assets" },
    { key: "inventory", label: "Inventory ops", to: "/infrastructure-leasing/inventory-operations" },
    { key: "create", label: "Create listing", to: "/infrastructure-leasing/create-listing" },
  ],
  logistics: [{ key: "rfqs", label: "Logistics RFQs", to: "/logistics-rfqs-sent" }],
  auctions: [
    { key: "listings", label: "My listings", to: "/auction/my/listings" },
    { key: "create", label: "Create auction", to: "/auction/create" },
  ],
  fulfilment: [
    { key: "orders", label: "Purchase orders", to: "/seller-purchase-order-list" },
    { key: "invoices", label: "Invoices", to: "/invoice-list" },
    { key: "shipping", label: "Shipping", to: "/shipping-manifest-generation-list" },
    { key: "grn", label: "GRN", to: "/approve-goods-receipt-list" },
  ],
};

export function buildSellerDashboardNavContext(p2pNav = null, options = {}) {
  const { p2pLoading = false } = options;
  const user = StorageService.getUser();
  const roles = getAvailableRoles();
  const currentRole = getCurrentRole();
  const p = p2pNav || {};
  const hasSellerRole = roles.some((r) => r.id === 2);
  const canAccessSellerDashboard = hasSellerRole && isSellerRoleSectionVisible(currentRole);

  return {
    user,
    roles,
    currentRole,
    p2pNav: p,
    p2pLoading,
    hasSellerRole,
    canAccessSellerDashboard,
    isSuperAdmin: !!p.is_super_admin,
    isCompanyAdmin: !!p.is_company_admin,
    canApprove: !!p.can_approve,
    canAccessCommandCentre: !!p.can_access_command_centre,
    canAccessPreapprovedVendors: !!p.can_access_preapproved_vendors,
    canSubmitServiceRequests: !!p.can_submit_service_requests,
    hasP2pOrgMembership: !!p.has_p2p_org_membership,
    /** Seller P2P module tab: org member or approver workflows only (not platform-admin diagnostics). */
    hasSellerP2pOperationalAccess: !!(
      p.has_p2p_org_membership || p.can_approve || p.can_access_sales_auction_controls
    ),
    /** Operational P2P access for overview cards — same rule as seller P2P tab. */
    hasP2pAccess: !!(
      p.has_p2p_org_membership || p.can_approve || p.can_access_sales_auction_controls
    ),
  };
}

function moduleVisible(mod, ctx) {
  if (!ctx.canAccessSellerDashboard) return false;
  if (typeof mod.visible === "function") return mod.visible(ctx);
  return true;
}

export function getVisibleSellerDashboardModules(ctx) {
  return SELLER_DASHBOARD_MODULES.filter((mod) => moduleVisible(mod, ctx)).map((mod) => {
    if (mod.key === "p2p") {
      return { ...mod, to: SELLER_DASHBOARD_MODULE_ROUTES.p2p };
    }
    return mod;
  });
}

export function getVisibleSellerDashboardModuleKeys(ctx) {
  return getVisibleSellerDashboardModules(ctx).map((m) => m.key);
}

const OVERVIEW_ALERT_MODULES = {
  pending_quote: ["rfq"],
  invoices: ["rfq", "fulfilment"],
  clarifications: ["rfq"],
  in_production: ["fulfilment"],
  payment_confirm: ["fulfilment"],
};

export function filterSellerDashboardOverviewData(data, ctx) {
  if (!data) return data;
  const visibleKeys = new Set(getVisibleSellerDashboardModuleKeys(ctx));

  const status_alerts = (data.status_alerts || []).filter((alert) => {
    const modules = OVERVIEW_ALERT_MODULES[alert.key];
    if (!modules) return true;
    return modules.some((k) => visibleKeys.has(k));
  });

  const module_summaries = (data.module_summaries || []).filter((mod) => visibleKeys.has(mod.key));

  return { ...data, status_alerts, module_summaries };
}

function pathMatchesModule(path, mod) {
  if (mod.key === "auctions") {
    if (path === "/auction") return false;
    return path.startsWith("/auction/");
  }
  const prefixes = [mod.to, ...(mod.pathPrefixes || [])];
  return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function getVisibleSellerDashboardSubTabs(moduleKey, ctx) {
  const tabs = SELLER_DASHBOARD_SUB_TABS[moduleKey] || [];
  return tabs.filter((tab) => (tab.visible ? tab.visible(ctx) : true));
}

export function resolveActiveSellerDashboardModule(pathname, modules) {
  const path = pathname || "";
  let best = modules.find((m) => m.key === "overview");
  let bestLen = 0;

  for (const mod of modules) {
    if (mod.key === "overview") {
      if (path === "/dashboard/seller" || path === "/dashboard/seller/") return mod;
      continue;
    }
    if (pathMatchesModule(path, mod)) {
      const prefixes = [mod.to, ...(mod.pathPrefixes || [])];
      const bestPrefix = prefixes
        .filter((prefix) => path === prefix || path.startsWith(`${prefix}/`))
        .sort((a, b) => b.length - a.length)[0];
      const len = bestPrefix?.length || 0;
      if (len >= bestLen) {
        best = mod;
        bestLen = len;
      }
    }
  }

  return best || modules[0];
}

export function isSubTabActive(pathname, tab) {
  const path = pathname || "";
  return path === tab.to || path.startsWith(`${tab.to}/`);
}
